import { useState, Fragment, useEffect } from "react";
import { Link } from "wouter";
import { useGetDeals } from "@workspace/api-client-react";
import { GoogleFeedAd } from "@/components/GoogleAd";
import { useSEO } from "@/hooks/use-seo";
import { useTown } from "@/hooks/use-town";
import FeedAdSlot from "@/components/FeedAdSlot";
import { useCategories } from "@/hooks/use-categories";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { SidebarFilter } from "@/components/SidebarFilter";
import { QueryState } from "@/components/QueryState";
import { useAnalytics } from "@/hooks/use-analytics";

const CATEGORY_COLOURS: Record<string, string> = {
  "food & drink": "bg-orange-100 text-orange-700",
  "hair & beauty": "bg-rose-100 text-rose-700",
  "fitness": "bg-green-100 text-green-700",
  "shopping": "bg-blue-100 text-blue-700",
  "cars": "bg-slate-100 text-slate-700",
  "home services": "bg-purple-100 text-purple-700",
  "entertainment": "bg-indigo-100 text-indigo-700",
};

function DaysLeft({ endDate }: { endDate: string }) {
  const today = new Date();
  const end = new Date(endDate);
  const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return <span className="text-xs text-slate-400">Expired</span>;
  if (diff === 0) return <span className="text-xs font-semibold text-red-600">⚠️ Expires today!</span>;
  if (diff <= 3) return <span className="text-xs font-semibold text-orange-600">⏰ {diff} days left</span>;
  return <span className="text-xs text-slate-400">Until {new Date(endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>;
}

function DealsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[1,2,3,4,5,6].map(i => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
          <div className="h-40 bg-slate-200" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-3 bg-slate-200 rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Deals() {
  useSEO({ title: "Local Deals & Discounts", description: "Save money with exclusive local deals and discounts from businesses across Ayrshire — Ayr, Kilmarnock, Irvine, Prestwick and more." });
  const { track } = useAnalytics();
  useEffect(() => { track("page_viewed", { screen: "deals" }); }, []);
  const { townParam, selectedTown, setSelectedTown } = useTown();
  const { user } = useAuth();
  const [category, setCategory] = useState("All");

  const { data, isLoading, isError, refetch } = useGetDeals({
    town: townParam,
    category: category === "All" ? undefined : category,
  });

  const deals = data?.items ?? [];
  const categories = ["All", ...useCategories("deal")];

  return (
    <div className="min-h-screen bg-slate-50">

      <div style={{ background: "linear-gradient(135deg, #0c1e33, #142d4c, #1a3a5c)" }} className="pt-8 pb-10 px-4">
        <div className="container mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Local Deals</h1>
            <p className="text-white/60 mt-1">Exclusive discounts from local businesses</p>
          </div>
          {user?.role === "business" && (
            <Link href="/business/deals">
              <Button size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/20">+ Add Deal</Button>
            </Link>
          )}
        </div>
      </div>

      <SidebarFilter items={categories} selected={category} onSelect={setCategory}>
        <div className="px-4 py-5">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={deals.length === 0}
          onRetry={refetch}
          skeleton={<DealsSkeleton />}
          errorTitle="Couldn't load deals"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          emptyIcon="🏷️"
          emptyTitle={townParam ? `No deals in ${selectedTown}.` : "No deals available right now."}
          emptyBody={townParam ? "There are no deals listed for this town yet." : "Check back soon or try a different category."}
          emptyAction={townParam ? { label: "See all Ayrshire deals", onClick: () => setSelectedTown("All Towns") } : undefined}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {deals.map((deal, i) => {
              const catColour = CATEGORY_COLOURS[deal.category?.toLowerCase() ?? ""] ?? "bg-amber-100 text-amber-700";
              return (
                <Fragment key={deal.id}>
                <Link
                  href={`/deals/${deal.slug ?? deal.id}`}
                  className="group block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  {deal.imageUrl ? (
                    <div className="relative h-40 overflow-hidden">
                      <img src={deal.imageUrl} alt={deal.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute top-3 left-3">
                        <span className="bg-secondary text-secondary-foreground text-xs font-bold px-2.5 py-1 rounded-full">DEAL</span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-secondary/20 to-amber-50 flex items-center justify-center relative">
                      <span className="text-5xl">🏷️</span>
                      <div className="absolute top-3 left-3">
                        <span className="bg-secondary text-secondary-foreground text-xs font-bold px-2.5 py-1 rounded-full">DEAL</span>
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    {deal.businessName ? (
                      <Link href={`/businesses?search=${encodeURIComponent(deal.businessName)}`} onClick={e => e.stopPropagation()} className="text-xs font-semibold text-primary hover:underline mb-1 block">{deal.businessName}</Link>
                    ) : (
                      <p className="text-xs font-semibold text-slate-500 mb-1">&nbsp;</p>
                    )}
                    <h3 className="font-bold text-slate-800 line-clamp-2 group-hover:text-primary transition-colors mb-2">{deal.title}</h3>
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
                      {deal.category && (
                        <span className={`font-semibold px-2 py-0.5 rounded-full capitalize ${catColour}`}>{deal.category}</span>
                      )}
                      <span>·</span>
                      <span>📍 {deal.town}</span>
                    </div>
                    <p className="text-slate-500 text-sm line-clamp-2 mb-3">{deal.description}</p>
                    <div className="flex items-center justify-between">
                      {deal.endDate && <DaysLeft endDate={deal.endDate} />}
                      <span className="text-xs text-primary font-semibold ml-auto">View deal →</span>
                    </div>
                  </div>
                </Link>
                {i === 2 && <div className="col-span-full"><GoogleFeedAd /></div>}
                {(i + 1) % 6 === 0 && i < deals.length - 1 && <div className="col-span-full"><FeedAdSlot placement="deals" town={townParam ? selectedTown : undefined} index={Math.floor(i / 6)} /></div>}
                </Fragment>
              );
            })}
          </div>
        </QueryState>
        </div>
      </SidebarFilter>
    </div>
  );
}
